import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormatTypes } from '@constants/templateDictionaries';
import TetraController from '@utility/TetraController';
import getToken from '@utility/getToken';
import Logger from '@utility/services/Logger';
import { toast } from 'react-toastify';
import exportPrefs from '../Input/exportPrefs';
import Actions from '../../../../../../../../../store/actions';
import PlaceHolder from '../../../../../../UI/Editor/EditSpace/EditorPane/RenderContent/placeholder';
import Export from '../ArrayItems/export';
import { isSafari } from '../../../../../../../../../utility/browsers';
import breakpointsMinWidth from '../../../../../../../../../styling/breakpointsMinWidth';
import getWordSelectionDoubleClick from '../utils/getWordSelectionDoubleClick';
import { universityRestriction } from '../../../../../../../../../utility/universityRestrictions';
import { contentGenerationEnabledSections } from '../../../Pad/contentGeneration/enums';
import { generateAIButtonTips } from '../utils/generateAIButtonTips';
import GenerateWithAIButton from './generateWithAIButton';
import { getClearText } from '../utils/getClearText';

const returnRoleDescriptions = ({ isFocused }) => {
  if (isFocused) {
    return css`
      border: 1px solid #8510df;
      box-sizing: border-box;
      border-radius: 4px;
    `;
  }
  return null;
};

const RoleDescriptionWrapper = styled.div`
  user-select: ${(props) => !props.isSafari && 'text !important'};
  -webkit-user-select: ${(props) => props.isSafari && 'text !important'};

  grid-column: 1/-1;
  border: 1px solid rgba(224, 224, 224, 1);
  min-height: 7rem;
  padding: 10px 10px 0 10px;
  text-align: left;
  font-size: 12px;
  line-height: 24px;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  color: #333d47;
  position: relative;
  border-radius: 4px;
  box-sizing: border-box;
  overflow-x: inherit;
  pointer-events: auto;
  cursor: text;
  word-break: break-word;

  ${(props) => returnRoleDescriptions(props)}
  ${(props) => {
    const { nodeType, isFocused } = props;
    if (nodeType === 'work_experience_role_achievements') {
      return css`
        min-height: 5rem;
      `;
    }
    if (nodeType === 'objective') {
      return css`
        margin-bottom: 0;
        padding-bottom: 0;
        min-height: 7rem;
      `;
    }
    if (nodeType === 'letter_body') {
      return css`
        margin-bottom: 0px;
        min-height: 14rem;
      `;
    }

    if (nodeType === 'summary') {
      return css`
        grid-column: 1/-1;
        justify-self: flex-start;
        background: #ffffff;
        border: ${isFocused
          ? `border: 1px solid #8510df`
          : `1px solid rgba(224, 224, 224, 1)`};
        height: auto;
        min-height: 24rem;
        box-sizing: border-box;
        border-radius: 4px;
        margin-bottom: 20px;
        width: 100%;
      `;
    }
    return null;
  }}

  @media ${breakpointsMinWidth.mobileMicroLg} {
    padding: 10px 12px 0 15px;
  }
  @media ${breakpointsMinWidth.tabletSm} {
    padding: 10px 11px 0 8px;
  }
  @media ${breakpointsMinWidth.tabletMd} {
    padding: 10px 8px 0 8px;
  }
  @media ${breakpointsMinWidth.tabletLg} {
    padding: 10px 10px 0 11px;
  }

  @media ${breakpointsMinWidth.desktopSm} {
    padding: 10px 10px 0 11px;
  }

  @media ${breakpointsMinWidth.desktopMd} {
    padding: 10px 14px 0 13px;
    font-size: 14px;
  }
  ${(props) => {
    const { sectionKey } = props;
    if (contentGenerationEnabledSections.includes(sectionKey)) {
      return css`
        padding: 24px 14px 0 13px;
        @media ${breakpointsMinWidth.mobileMicroLg} {
          padding: 24px 12px 0 15px;
        }
        @media ${breakpointsMinWidth.tabletSm} {
          padding: 24px 11px 0 8px;
        }
        @media ${breakpointsMinWidth.tabletMd} {
          padding: 24px 8px 0 8px;
        }
        @media ${breakpointsMinWidth.tabletLg} {
          padding: 24px 10px 0 11px;
        }

        @media ${breakpointsMinWidth.desktopSm} {
          padding: 24px 10px 0 11px;
        }

        @media ${breakpointsMinWidth.desktopMd} {
          padding: 24px 14px 0 13px;
          font-size: 14px;
        }
      `;
    }
    return null;
  }}
`;

const PlaceHolder2 = styled(PlaceHolder)`
  ${(props) => {
    const { nodeText, isFocused } = props;
    if (!isFocused && !nodeText) {
      return css`
        top: 16px;
        transition: top 0.3s ease-in-out;
      `;
    }

    return null;
  }}
`;
const DisclaimerText = styled.div`
  font-size: 12px;
  text-align: start;
  bottom: -50px;
  left: 0px;
  position: absolute;
  line-height: 20px;
  color: gray;
`;

class RoleDescription extends Component {
  state = {};

  componentDidUpdate = (prevProps) => {
    const {
      nodeProps: {
        node: { text, type },
      },
      setUserPreferences,
      userResumePreferences,
    } = this.props;

    const {
      nodeProps: {
        node: { text: prevText },
      },
    } = prevProps;

    if (type === 'objective' && text !== prevText) {
      exportPrefs(type, userResumePreferences, text, setUserPreferences);
    }
  };

  render() {
    const {
      nodeProps,
      formatType,
      isTitleEditing,
      universityPlan,
      placeHolder,
      setUserPreferences,
      userResumePreferences,
      documentId,
      updateGeneratedData,
      sectionKey,
      addGeneratedData,
      isGenerating,
      sectionData,
      saveDocument,
      contentGenerationData,
    } = this.props;
    const { node, attributes, children, editor, isFocused } = nodeProps;

    const {
      value: { focusBlock },
    } = editor;
    const nodeKey = node?.key;
    const path = editor?.value?.document?.getPath(nodeKey)?.toJS();
    const currentRole = node && sectionData?.document?.getParent(node?.key);

    const currentOrganization =
      currentRole && sectionData?.document?.getParent(currentRole?.key);

    const checkIfGenerateRangeCrossed = () => {
      if (contentGenerationData?.[sectionKey]?.length >= 10) {
        return true;
      }
      return false;
    };

    const checkIfContentGenerationEnabled = () => {
      const orgName = getClearText(
        currentOrganization?.nodes?.toArray()?.[0]?.nodes?.toArray()?.[1]?.text,
      );

      const title = getClearText(currentRole?.nodes?.toArray()?.[0]?.text);
      let fieldOfStudy;
      if (sectionKey === 'summary') {
        return { isEnabled: true };
      }
      if (sectionKey === 'education') {
        fieldOfStudy = getClearText(currentRole?.nodes?.toArray()?.[2]?.text);
        return {
          isEnabled:
            orgName &&
            orgName !== '' &&
            title &&
            title !== '' &&
            fieldOfStudy &&
            fieldOfStudy !== '',
          orgName,
          title,
          fieldOfStudy,
        };
      }
      return {
        isEnabled: orgName && orgName !== '' && title && title !== '',
        orgName,
        title,
      };
    };

    const {
      orgName,
      title,
      fieldOfStudy,
      isEnabled: isEnableButton,
    } = checkIfContentGenerationEnabled();

    const isGenerateRangeCrossed = checkIfGenerateRangeCrossed();

    const toolTipContent = generateAIButtonTips({
      orgName,
      title,
      fieldOfStudy,
      sectionKey,
      isGenerateRangeCrossed,
    });

    const handleClick = async () => {
      if (isGenerating || !isEnableButton || isGenerateRangeCrossed) return;
      setUserPreferences({
        userPreferences: {
          ...userResumePreferences,
          layout: {
            ...userResumePreferences.layout,
            tetraScan: false,
            previewPad: false,
            tipsPad: false,
            veteranTips: false,
            galleryPad: false,
            galleryPadFullScreen: false,
            menuBar: true,
            linkedInOptimizer: false,
            questionsGenerator: false,
            aiCareerPath: false,
            contentGeneration: true,
          },
        },
        formatType: formatType || FormatTypes.RESUME,
      });

      addGeneratedData({ startGenerating: true });
      let data = {};
      if (sectionKey !== 'summary') {
        const allOrgs = editor?.value?.document?.nodes?.toArray();
        const organizationIndex = allOrgs.findIndex(
          (org) => org?.key === currentOrganization?.key,
        );
        const foundedOrg = allOrgs[organizationIndex];
        const orgNodes = foundedOrg?.nodes?.toArray();
        const allRoles = orgNodes.slice(1);
        const roleIndex = allRoles.findIndex(
          (role) => role.key === currentRole.key,
        );
        data.path = [organizationIndex, roleIndex];
      }
      data = {
        ...data,
        sectionKey,
        documentId,
        universityPlan,
        formatType: formatType || FormatTypes.RESUME,
      };
      let res;
      try {
        await saveDocument();
        const token = await getToken();
        res = await TetraController.generateContent(data, token);
      } catch (error) {
        toast.error('Failed to generate content');
        Logger.LogError(error);
      } finally {
        updateGeneratedData({ sectionKey, sectionData: res });
        addGeneratedData({ startGenerating: false });
      }
    };

    const { nodes: childrenNodes } = node;
    let noPlaceHolder = false;
    if (childrenNodes.size === 1) {
      const childrenNodesArray = childrenNodes.toArray();
      const [firstNode] = childrenNodesArray;
      if (firstNode.type === 'unordered_list') {
        noPlaceHolder = true;
      }
    }

    return (
      <RoleDescriptionWrapper
        {...attributes}
        nodeType={node.type}
        isFocused={isFocused}
        aria-label={node.type}
        onMouseDown={(e) => {
          if (e.detail === 2) {
            getWordSelectionDoubleClick({ editor });
          }
          if (e.detail >= 3) {
            e.preventDefault();
            e.stopPropagation();
            editor.moveToRangeOfNode(focusBlock);
          }
        }}
        isSafari={isSafari}
        data-testid={`${node.type}-${path?.join('-')}`}
        sectionKey={sectionKey}
      >
        {children}

        <div>
          <PlaceHolder2
            contentEditable={false}
            isFocused={isFocused}
            nodeText={node.text}
            noPlaceHolder={noPlaceHolder}
            nodeType={node.type}
          >
            {placeHolder}
          </PlaceHolder2>
        </div>

        {formatType === FormatTypes.RESUME &&
          contentGenerationEnabledSections.includes(sectionKey) &&
          node?.type !== 'work_experience_role_achievements' && (
            <GenerateWithAIButton
              toolTipContent={toolTipContent}
              isGenerating={isGenerating}
              handleClick={handleClick}
              isEnableButton={isEnableButton}
              isGenerateRangeCrossed={isGenerateRangeCrossed}
            />
          )}

        {node.type === 'summary' && (
          <>
            <Export
              editor={editor}
              node={node}
              formatType={formatType}
              isTitleEditing={isTitleEditing}
              dataTestId="summary"
            />
            {universityRestriction[universityPlan?.code]?.editor?.resume
              ?.summary?.disclaimer && (
              <DisclaimerText>
                {`Disclaimer : ${
                  universityRestriction[universityPlan?.code]?.editor?.resume
                    ?.summary?.disclaimer
                }`}
              </DisclaimerText>
            )}
          </>
        )}
      </RoleDescriptionWrapper>
    );
  }
}

RoleDescription.propTypes = {
  nodeProps: propTypes.objectOf(propTypes.object).isRequired,
  attributes: propTypes.objectOf(propTypes.string).isRequired,
  setUserPreferences: propTypes.func.isRequired,
  userResumePreferences: propTypes.objectOf(propTypes.object).isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  userResumePreferences: state.editResumeFormReducer.userPreferences,
  documentId: state.editResumeFormReducer.documentId,
  isGenerating: state.contentGeneration.contentGenerationData.startGenerating,
  sectionData: state.editResumeFormReducer[ownProps?.sectionKey],
  contentGenerationData: state.contentGeneration.contentGenerationData,
});

const mapDispatchToProps = (dispatch) => ({
  setUserPreferences: (params) =>
    dispatch(Actions.editDocumentFormActions.updateUserPreferences(params)),
  updateGeneratedData: (params) =>
    dispatch(Actions.contentGenerationAction.updateGeneratedData(params)),
  addGeneratedData: (params) =>
    dispatch(Actions.contentGenerationAction.addGeneratedData(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RoleDescription);
